import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import { Product } from '@/types';
import { getCategoryIcon, BUILT_IN_CATEGORIES } from '@/lib/constants';

// Categories handled by individual product selection (not aggregated)
const SPECIAL_CATEGORIES = ['FLOWER', 'PREROLL'] as const;

// Categories that get aggregated input (everything except special categories)
const AGGREGATE_CATEGORIES = BUILT_IN_CATEGORIES.filter(
  (c) => !SPECIAL_CATEGORIES.includes(c as typeof SPECIAL_CATEGORIES[number])
);
import { haptic } from '@/lib/haptics';

interface CountData {
  productId: string;
  quantity: number;
}

interface SalesModalProps {
  isOpen: boolean;
  onClose: () => void;
  products: Product[];
  expectedSales?: number;
  morningCounts?: CountData[];
  eveningCounts?: CountData[];
  restocks?: CountData[];
  existingSales?: Array<{ id: string; productId: string; quantity: number; totalAmount: number }>;
  onSubmit: (data: SalesData) => void;
  isEditMode?: boolean;
}

interface SalesData {
  totalAmount?: number;
  paymentMethod: 'CASH' | 'CARD' | 'EFT';
  productSales?: Array<{ productId: string; quantity: number }>;
  notes?: string;
}

export function SalesModal({ isOpen, onClose, products, expectedSales = 0, morningCounts = [], eveningCounts = [], restocks = [], existingSales = [], onSubmit, isEditMode = false }: SalesModalProps) {
  // Get all pre-roll products for individual tracking
  const prerollProducts = products.filter(p => p.category === 'PREROLL');

  // Build initial sales state (string-based to preserve "0" input)
  const getInitialSales = (): Record<string, string> => {
    const initial: Record<string, string> = {
      aaaFlower: '',
      greendoorFlower: '',
    };

    AGGREGATE_CATEGORIES.forEach((cat) => {
      initial[cat] = '';
    });

    prerollProducts.forEach((product) => {
      initial[`preroll_${product.id}`] = '';
    });

    // If in edit mode, populate with existing sales data
    if (isEditMode && existingSales.length > 0) {
      const numericTotals: Record<string, number> = {};
      existingSales.forEach(sale => {
        const product = products.find(p => p.id === sale.productId);
        if (product) {
          if (product.category === 'FLOWER') {
            if (product.quality === 'PREMIUM') {
              numericTotals.aaaFlower = (numericTotals.aaaFlower || 0) + sale.quantity;
            } else if (product.quality === 'HIGH') {
              numericTotals.greendoorFlower = (numericTotals.greendoorFlower || 0) + sale.quantity;
            }
          } else if (product.category === 'PREROLL') {
            numericTotals[`preroll_${product.id}`] = sale.quantity;
          } else {
            numericTotals[product.category] = (numericTotals[product.category] || 0) + sale.quantity;
          }
        }
      });
      // Convert non-zero values to strings for display
      Object.entries(numericTotals).forEach(([key, val]) => {
        if (val > 0) initial[key] = val.toString();
      });
    }

    return initial;
  };

  const [categorySales, setCategorySales] = useState<Record<string, string>>(getInitialSales);
  const [notes, setNotes] = useState('');

  // Reinitialize when modal opens (handles edit mode prefill)
  useEffect(() => {
    if (isOpen) {
      setCategorySales(getInitialSales());
      setNotes('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Get list of categories that have active products
  const getActiveCategories = (): string[] => {
    const categoriesSet = new Set<string>();
    products.forEach(p => categoriesSet.add(p.category));
    return Array.from(categoriesSet).sort();
  };

  // Helper to get numeric value from string state
  const getNumeric = (key: string): number => parseFloat(categorySales[key]) || 0;

  // Calculate total units from category sales
  const calculateTotalUnits = (): number => {
    return Object.values(categorySales).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const totalUnits = calculateTotalUnits();

  const handleSubmit = () => {
    const data: SalesData = {
      paymentMethod: 'CARD', // Default to card for end-of-day totals
      notes: notes || undefined,
    };

    // Categories tab: distribute to individual products proportionally
    const distributedSales: Array<{ productId: string; quantity: number }> = [];

    // Create maps for quick lookup
    const morningMap = new Map(morningCounts.map(c => [c.productId, c.quantity]));
    const eveningMap = new Map(eveningCounts.map(c => [c.productId, c.quantity]));
    const restockMap = new Map(restocks.map(c => [c.productId, c.quantity]));

    // Helper function to distribute quantity to products based on movement
    const distributeToProducts = (
      quantity: number,
      productList: typeof products
    ) => {
      if (quantity === 0 || productList.length === 0) return;

      // Calculate actual movement for each product
      const productExpectations = productList.map(product => {
        const morning = morningMap.get(product.id) || 0;
        const evening = eveningMap.get(product.id) || 0;
        const restock = restockMap.get(product.id) || 0;
        const movement = morning + restock - evening;
        return { product, movement };
      }).filter(p => p.movement > 0 && !p.product.isAccumulating);

      const totalMovement = productExpectations.reduce((sum, p) => sum + p.movement, 0);

      if (totalMovement === 0) return;

      // Calculate proportional distribution for each product
      const distributions = productExpectations.map(({ product, movement }) => {
        const proportion = movement / totalMovement;
        const exactQty = quantity * proportion;
        const roundedQty = Math.round(exactQty);
        return { product, movement, exactQty, roundedQty };
      });

      // Calculate initial total and adjust for rounding errors
      let totalRounded = distributions.reduce((sum, d) => sum + d.roundedQty, 0);
      let diff = quantity - totalRounded;

      // Sort by how much rounding hurt/helped each product (fractional part)
      // Products that were rounded down the most should get priority for +1 adjustments
      const sortedByFraction = [...distributions].sort((a, b) => {
        const aFrac = a.exactQty - a.roundedQty;
        const bFrac = b.exactQty - b.roundedQty;
        return bFrac - aFrac; // Descending: most hurt by rounding first
      });

      // Adjust to match exact quantity
      for (const dist of sortedByFraction) {
        if (diff === 0) break;

        if (diff > 0 && dist.roundedQty >= 0) {
          dist.roundedQty += 1;
          diff -= 1;
        } else if (diff < 0 && dist.roundedQty > 0) {
          dist.roundedQty -= 1;
          diff += 1;
        }
      }

      // Create sales records for all products with quantity > 0
      for (const { product, roundedQty } of distributions) {
        if (roundedQty > 0) {
          distributedSales.push({ productId: product.id, quantity: roundedQty });
        }
      }
    };

    // Distribute AAA (PREMIUM FLOWER)
    const aaaQty = getNumeric('aaaFlower');
    if (aaaQty > 0) {
      const aaaProducts = products.filter(p => p.quality === 'PREMIUM' && p.category === 'FLOWER' && !p.isAccumulating);
      distributeToProducts(aaaQty, aaaProducts);
    }

    // Distribute Greendoor (HIGH FLOWER)
    const greendoorQty = getNumeric('greendoorFlower');
    if (greendoorQty > 0) {
      const greendoorProducts = products.filter(p => p.quality === 'HIGH' && p.category === 'FLOWER' && !p.isAccumulating);
      distributeToProducts(greendoorQty, greendoorProducts);
    }

    // Handle individual pre-roll products
    Object.entries(categorySales).forEach(([key, val]) => {
      const quantity = parseFloat(val) || 0;
      if (key.startsWith('preroll_') && quantity > 0) {
        const productId = key.replace('preroll_', '');
        const product = products.find(p => p.id === productId);
        if (product) {
          distributedSales.push({ productId, quantity });
        }
      }
    });

    // Distribute other categories (excluding FLOWER/PREROLL which are handled specially)
    for (const key of AGGREGATE_CATEGORIES) {
      const quantity = getNumeric(key);
      if (quantity > 0) {
        const categoryProducts = products.filter(p => p.category === key);
        distributeToProducts(quantity, categoryProducts);
      }
    }

    data.productSales = distributedSales;
    data.notes = notes || `Category Sales: AAA=${aaaQty}g, Greendoor=${greendoorQty}g`;

    onSubmit(data);
    // Reset form - rebuild initial state with pre-roll products
    const resetSales: Record<string, string> = {
      aaaFlower: '',
      greendoorFlower: '',
    };
    AGGREGATE_CATEGORIES.forEach((cat) => {
      resetSales[cat] = '';
    });
    prerollProducts.forEach((product) => {
      resetSales[`preroll_${product.id}`] = '';
    });
    setCategorySales(resetSales);
    setNotes('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-end">
      <div className="bg-dark w-full max-h-[90vh] rounded-t-3xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-dark-card border-b border-primary/20 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-gold">
              📊 {isEditMode ? 'Edit Sales' : 'Record Sales'} (Yoco Data)
            </h2>
            <button
              onClick={() => { haptic.light(); onClose(); }}
              className="text-gray-400 hover:text-white text-2xl transition-all active:scale-[0.92] p-2 -m-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="space-y-4">
            {/* FLOWER Category - Split into AAA and Greendoor */}
            <div className="bg-dark-card rounded-lg p-4 border-2 border-primary/30">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">{getCategoryIcon('FLOWER')}</span>
                <h3 className="font-bold text-transparent bg-clip-text bg-gradient-gold">
                  Flower
                </h3>
              </div>

              {/* AAA Flower */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  AAA (Premium)
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={categorySales.aaaFlower ?? ''}
                    onChange={(e) => setCategorySales(prev => ({ ...prev, aaaFlower: e.target.value }))}
                    placeholder="0"
                    className="text-center text-lg font-bold"
                  />
                  <span className="text-gray-400 text-sm">grams</span>
                </div>
              </div>

              {/* Greendoor Flower */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Greendoor
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={categorySales.greendoorFlower ?? ''}
                    onChange={(e) => setCategorySales(prev => ({ ...prev, greendoorFlower: e.target.value }))}
                    placeholder="0"
                    className="text-center text-lg font-bold"
                  />
                  <span className="text-gray-400 text-sm">grams</span>
                </div>
              </div>
            </div>

            {/* Pre-roll Products - Individual Tracking */}
            {prerollProducts.length > 0 && (
              <div className="bg-dark-card rounded-lg p-4 border-2 border-primary/30">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">{getCategoryIcon('PREROLL')}</span>
                  <h3 className="font-bold text-transparent bg-clip-text bg-gradient-gold">
                    Pre-rolls (Individual Products)
                  </h3>
                </div>

                <div className="space-y-2">
                  {prerollProducts.map(product => {
                    const key = `preroll_${product.id}`;

                    return (
                      <div key={product.id} className="flex items-center justify-between gap-2">
                        <label className="text-sm font-medium text-gray-300 flex-1">
                          {product.name}
                        </label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            value={categorySales[key] ?? ''}
                            onChange={(e) => setCategorySales(prev => ({ ...prev, [key]: e.target.value }))}
                            placeholder="0"
                            className="text-center text-lg font-bold w-24"
                          />
                          <span className="text-gray-400 text-sm">units</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Other Active Categories (excluding FLOWER and PREROLL) */}
            {getActiveCategories()
              .filter(cat => cat !== 'FLOWER' && cat !== 'PREROLL')
              .map(category => (
                  <div key={category} className="bg-dark-card rounded-lg p-4 border-2 border-gray-700">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">{getCategoryIcon(category)}</span>
                      <h3 className="font-semibold text-white capitalize">
                        {category.toLowerCase()}
                      </h3>
                    </div>

                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={categorySales[category] ?? ''}
                        onChange={(e) => setCategorySales(prev => ({ ...prev, [category]: e.target.value }))}
                        placeholder="0"
                        className="text-center text-lg font-bold"
                      />
                      <span className="text-gray-400 text-sm">units</span>
                    </div>
                  </div>
              ))}

            {/* Total Units Display */}
            {totalUnits > 0 && (
              <div className="bg-gradient-gold rounded-lg p-4 sticky bottom-0">
                <div className="flex justify-between items-center">
                  <span className="text-dark font-bold">Total Units:</span>
                  <span className="text-dark font-bold text-2xl">
                    {totalUnits.toFixed(1)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional details..."
              rows={2}
              className={cn(
                'w-full px-3 py-2 bg-dark-lighter border border-gray-700 rounded-md shadow-sm',
                'text-white placeholder-gray-500',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary',
                'resize-none'
              )}
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="p-4 bg-dark-card border-t border-primary/20">
          <Button
            variant="primary"
            size="lg"
            onClick={handleSubmit}
            disabled={!Object.values(categorySales).some(v => v !== '')}
            className="w-full"
          >
            {isEditMode ? 'Update' : 'Record'} Sales - {totalUnits.toFixed(1)} units
          </Button>
        </div>
      </div>
    </div>
  );
}